import { IDayCell } from './calendar'
import { toJalaali } from './jalaali'
import { toHijri } from './hijri'
import { ICustomOccasion, Reminder, repeatsOn } from './occasion-repeat'
import { IEvent, occurrencesInRange } from './event-repeat'

// When a reminder should go off, as an ordinary Gregorian instant.
//
// This is the whole of the calendar work behind notifications. The operating
// system only understands absolute times, and it can only repeat on the
// Gregorian calendar — which is no use to an anniversary kept in Jalaali or
// hijri months. So nothing here is handed to the OS as a rule. Each occurrence
// is worked out in the calendar the user chose, converted to a Gregorian
// date-time, and scheduled as a one-off.
//
// Pure: no storage, no plugin, no DOM. The scheduler that uses it is in
// services/reminders.ts.

// An occasion has a date but no time, so a reminder counts back from this hour
// on the day itself. "One day before" therefore lands at 9am the day before.
export const DAY_HOUR = 9

// How far before the day each reminder fires, as calendar steps rather than a
// span of milliseconds: a week earlier means the same hour seven days back,
// which stays 9am across a daylight-saving change. Subtracting 168 hours would
// land at 8am on the weeks the clocks move.
const LEAD: Record<Reminder, { days: number, hours: number }> = {
    none: { days: 0, hours: 0 },
    week: { days: 7, hours: 0 },
    day: { days: 1, hours: 0 },
    hour: { days: 0, hours: 1 },
}

// A reminder and the day it is about — which are different days, and the day is
// the one the notification should open.
export interface IReminderAt {
    occurrence: Date
    at: Date
}

const cellOf = (date: Date): IDayCell => {
    const gy = date.getFullYear()
    const gm = date.getMonth() + 1
    const gd = date.getDate()
    return { ...toJalaali(gy, gm, gd), gy, gm, gd, ...toHijri(gy, gm, gd), inMonth: true, isToday: false }
}

const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

// Local time throughout: 9am means 9am where the phone is. The Date
// constructor normalises a negative day or hour across month and year
// boundaries, so no arithmetic of our own is needed.
export function reminderAt(occurrence: Date, reminder: Reminder): Date {
    const { days, hours } = LEAD[reminder]
    return new Date(
        occurrence.getFullYear(),
        occurrence.getMonth(),
        occurrence.getDate() - days,
        DAY_HOUR - hours)
}

// The next days this occasion falls on, starting from `from`.
//
// It walks the days and asks the same question the day page asks, rather than
// doing calendar arithmetic of its own. That is slower — a few hundred cheap
// conversions — but it cannot disagree with what the calendar shows, including
// a hijri anniversary that a moon-sighting correction has just moved.
export function nextOccurrences(o: ICustomOccasion, from: Date, count: number): Date[] {
    const wanted = o.repeat === 'none' ? 1 : count
    const start = midnight(from)
    const out: Date[] = []
    // A hijri year is the shortest at ~355 days, a Jalaali or Gregorian one 366.
    // 400 a piece leaves room for the clamp onto a short month's last day.
    const days = 400 * wanted
    for (let i = 0; i <= days && out.length < wanted; i++) {
        const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
        if (repeatsOn(o, cellOf(day))) out.push(day)
    }
    return out
}

// The occasion's coming reminders: those still due at or after `from`.
export function occasionReminders(o: ICustomOccasion, from: Date, count: number): IReminderAt[] {
    if (o.reminder === 'none') return []
    const out: IReminderAt[] = []
    // One spare: the nearest occurrence may already have had its reminder pass.
    for (const occurrence of nextOccurrences(o, from, count + 1)) {
        const at = reminderAt(occurrence, o.reminder)
        if (at.getTime() >= from.getTime()) out.push({ occurrence, at })
        if (out.length >= count) break
    }
    return out
}

// A task with no clock has nothing for an hour's lead to count back from, so
// an 'hour' on one reads as the day before. The wizard only offers that lead
// once a time has been picked; this is for the tasks saved before times
// existed, whose stored reminder is left exactly as they set it.
export const effectiveReminder = (reminder: Reminder, time?: string): Reminder =>
    !time && reminder === 'hour' ? 'day' : reminder

// A task sits on one day and never repeats, so there is nothing to search for.
export function dateReminder(
    gy: number, gm: number, gd: number, reminder: Reminder, from: Date
): IReminderAt[] {
    if (reminder === 'none') return []
    const occurrence = new Date(gy, gm - 1, gd)
    const at = reminderAt(occurrence, reminder)
    return at.getTime() >= from.getTime() ? [{ occurrence, at }] : []
}

// --- Events ---
//
// An event is not an occasion: it has a real time of day, and it repeats on the
// Gregorian calendar. So both halves of the work below are simpler. The lead
// counts back from the start itself rather than from the day's nine o'clock,
// and the occurrences can be worked out rather than searched for a day at a
// time — see services/event-repeat.ts.

export function instantReminder(start: Date, reminder: Reminder, from: Date): IReminderAt[] {
    if (reminder === 'none') return []
    const { days, hours } = LEAD[reminder]
    const at = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() - days,
        start.getHours() - hours,
        start.getMinutes())
    return at.getTime() >= from.getTime() ? [{ occurrence: start, at }] : []
}

// The event's coming reminders: those still due at or after `from`.
export function eventReminders(e: IEvent, from: Date, count: number): IReminderAt[] {
    if (e.reminder === 'none') return []
    // A year and a bit past `from`, the same horizon the rest of the scheduler
    // works to.
    const to = new Date(from.getFullYear() + 1, from.getMonth() + 2, from.getDate()).getTime()
    const out: IReminderAt[] = []
    for (const occ of occurrencesInRange(e, from.getTime(), to)) {
        // An occurrence already under way, or one so close that its lead has
        // gone by, has nothing left to announce.
        const [due] = instantReminder(new Date(occ.start), e.reminder, from)
        if (due) out.push(due)
        if (out.length >= count) break
    }
    return out
}
